import React, { useState, useEffect } from 'react';
import { 
  Satellite, Flame, Radio, Maximize2, ExternalLink, ShieldCheck, 
  Sparkles, CheckCircle2, FileText, ArrowRight, Layers, Globe, 
  AlertTriangle, RefreshCw, Zap, Award, Check, Lock, Clock, ShieldAlert,
  HelpCircle, Download
} from 'lucide-react';
import { Language } from '../utils/i18n';

interface SituationalCenterShowcaseProps {
  language: Language;
  onSelectAgent?: (agentId: string) => void;
  onOpenPricing?: () => void;
  isLoggedIn?: boolean;
  currentUserEmail?: string;
  onNavigateToLogin?: () => void;
}

export const SituationalCenterShowcase: React.FC<SituationalCenterShowcaseProps> = ({
  language,
  onSelectAgent,
  onOpenPricing,
  isLoggedIn = false,
  currentUserEmail,
  onNavigateToLogin
}) => {
  const [activeMapTab, setActiveMapTab] = useState<'situational' | 'nis_eco'>('situational');
  const [fullscreenNotice, setFullscreenNotice] = useState<string | null>(null);

  // Map access telemetry state
  const [mapAccess, setMapAccess] = useState<{
    checked: boolean;
    hasAccess: boolean;
    trialActive: boolean;
    trialHoursRemaining: number;
    isSubscribed: boolean;
    reason?: string;
  }>({
    checked: false,
    hasAccess: false,
    trialActive: false,
    trialHoursRemaining: 0,
    isSubscribed: false
  });

  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = !isUk && !isPl;

  // Check map access (free access with bass123123 or session)
  useEffect(() => {
    fetch('/api/map/access-status')
      .then(res => res.json())
      .then(data => {
        if (data.hasAccess) {
          setMapAccess({
            checked: true,
            hasAccess: true,
            trialActive: false,
            trialHoursRemaining: 999999,
            isSubscribed: true,
            reason: data.reason
          });
        } else {
          // Check local storage
          const hasLocalToken = typeof window !== 'undefined' && localStorage.getItem('sit_map_auth') === 'bass123123';
          setMapAccess({
            checked: true,
            hasAccess: hasLocalToken,
            trialActive: false,
            trialHoursRemaining: hasLocalToken ? 999999 : 0,
            isSubscribed: hasLocalToken,
            reason: hasLocalToken ? 'Вільний доступ' : (data.reason || 'Потрібен пароль швидкого доступу')
          });
        }
      })
      .catch(() => {
        const hasLocalToken = typeof window !== 'undefined' && localStorage.getItem('sit_map_auth') === 'bass123123';
        setMapAccess({
          checked: true,
          hasAccess: hasLocalToken,
          trialActive: false,
          trialHoursRemaining: hasLocalToken ? 999999 : 0,
          isSubscribed: hasLocalToken,
          reason: 'Вільний оперативний доступ'
        });
      });
  }, [isLoggedIn, currentUserEmail]);

  const handleOrderPlan = (agentId: string) => {
    if (onSelectAgent) {
      onSelectAgent(agentId);
    } else if (onOpenPricing) {
      onOpenPricing();
    } else {
      handleGoToLogin();
    }
  };

  const handleGoToLogin = () => {
    if (onNavigateToLogin) {
      onNavigateToLogin();
    } else {
      const loginSection = document.getElementById('login-card-section');
      if (loginSection) {
        loginSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleFullscreenClick = (e: React.MouseEvent) => {
    if (!isLoggedIn || !mapAccess.hasAccess) {
      e.preventDefault();
      setFullscreenNotice(
        isUk 
          ? '🔒 Повноекранний інтерактивний доступ відкривається після входу. Для нових користувачів діє 1 день 100% безкоштовно!' 
          : isPl 
          ? '🔒 Pełnoekranowy dostęp do mapy otwiera się po zalogowaniu. 1 dzień gratis!' 
          : '🔒 Fullscreen interactive map activates after login. 1-day free trial included!'
      );
      setTimeout(() => {
        handleGoToLogin();
      }, 900);
    }
  };

  return (
    <div className="w-full bg-slate-900 border border-cyan-500/30 rounded-3xl overflow-hidden shadow-2xl p-4 sm:p-6 md:p-8 space-y-8 relative text-left">
      {/* Background glow accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Block */}
      <div className="space-y-4 relative z-10">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-950/80 border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-wider shadow-inner">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"></span>
          </span>
          🛰️ {isUk ? 'ОПЕРАТИВНИЙ СИТУАЦІЙНИЙ ЦЕНТР ТА СУПУТНИКОВИЙ МОНІТОРИНГ 24/7' : isPl ? 'CENTRUM SYTUACYJNE I MONITORING SATELITARNY 24/7' : 'OPERATIONAL SITUATIONAL CENTER & SATELLITE RADAR 24/7'}
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              {isUk ? 'Оперативні Карти Загроз, РЕБ та Земельного Аудиту' : isPl ? 'Mapy Zagrożeń Operacyjnych, WRE i Audytu Gruntów' : 'Operational Threat Radar, EW Jamming & Satellite Land Audit'}
            </h2>
            <p className="text-sm sm:text-base text-slate-300 max-w-4xl leading-relaxed">
              {isUk ? 'Супутникові термальні спалахи NASA FIRMS, карти радіоелектронного глушіння GPSJam, спектральні індекси «НІС-ЕКО» (Sentinel-2 NDVI/NDWI) та сейсмоконтроль детонацій і вибухів.' : 
               isPl ? 'Transmisja anomalii termicznych NASA FIRMS, zakłóceń GPSJam, wskaźników spektralnych NIS-EKO (Sentinel-2 NDVI/NDWI) i kontroli sejsmicznej.' : 
               'Live telemetry from NASA FIRMS thermal anomaly feeds, GPSJam electronic warfare interference, NIS-ECO Sentinel-2 spectral indices (NDVI/NDWI), and seismic detonation monitoring.'}
            </p>
          </div>

          {/* Real-time telemetry status badge strip */}
          <div className="flex flex-wrap items-center gap-2 text-xs shrink-0">
            <span className="px-2.5 py-1 rounded-lg bg-emerald-950/80 border border-emerald-500/40 text-emerald-400 font-mono font-medium flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-amber-400" /> NASA FIRMS: 1 420+ вогнищ
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-blue-950/80 border border-blue-500/40 text-blue-400 font-mono font-medium flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-blue-300 animate-pulse" /> РЕБ GPSJam: Активно
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-cyan-950/80 border border-cyan-500/40 text-cyan-400 font-mono font-medium flex items-center gap-1.5">
              <Satellite className="w-3.5 h-3.5 text-cyan-300" /> Sentinel-2: L2A 10m
            </span>
          </div>
        </div>
      </div>

      {/* Map Switcher Tabs */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-slate-800 pb-3 relative z-10">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveMapTab('situational')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all shadow-md cursor-pointer ${
              activeMapTab === 'situational'
                ? 'bg-gradient-to-r from-red-600 to-rose-700 text-white shadow-rose-900/40 border border-rose-400/50'
                : 'bg-slate-800/90 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-700/60'
            }`}
          >
            <Flame className="w-4 h-4 text-amber-300" />
            <span>{isUk ? '🎯 Карта Загроз ТВД (FIRMS + РЕБ + Фронт)' : isPl ? '🎯 Mapa Zagrożeń (FIRMS + WRE)' : '🎯 Operational Theater Map (FIRMS + EW)'}</span>
          </button>

          <button
            onClick={() => setActiveMapTab('nis_eco')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all shadow-md cursor-pointer ${
              activeMapTab === 'nis_eco'
                ? 'bg-gradient-to-r from-cyan-600 to-blue-700 text-white shadow-cyan-900/40 border border-cyan-400/50'
                : 'bg-slate-800/90 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-700/60'
            }`}
          >
            <Satellite className="w-4 h-4 text-cyan-300" />
            <span>{isUk ? '🛰️ Супутниковий Моніторинг «НІС-ЕКО» (NDVI/ДЗЗ)' : isPl ? '🛰️ Satelitarny Monitoring «NIS-EKO»' : '🛰️ Satellite Land Monitoring NIS-ECO'}</span>
          </button>
        </div>

        {/* Buttons right side: Download Windows Shortcut & Fullscreen */}
        <div className="flex items-center gap-2">
          <a
            href={activeMapTab === 'situational' ? '/api/map/shortcut/situational.url' : '/api/map/shortcut/nis-eco.url'}
            download
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-emerald-400 border border-emerald-500/30 rounded-xl text-xs font-bold transition-all shadow-sm group cursor-pointer"
            title={isUk ? 'Завантажити готовий ярлик на Робочий стіл Windows (.url)' : 'Download Windows Desktop Shortcut'}
          >
            <Download className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            <span className="hidden sm:inline">{isUk ? 'Ярлик на стіл' : 'Desktop Shortcut'}</span>
          </a>

          <a
            href={(activeMapTab === 'situational' ? '/situational-map' : '/nis-eco-map') + '?pwd=bass123123'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg group cursor-pointer"
            title={isUk ? 'Відкрити карту у новій вкладці (вхід без реєстрації)' : 'Open Fullscreen in New Tab'}
          >
            <Maximize2 className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            <span>{isUk ? 'Відкрити на весь екран' : isPl ? 'Otwórz na pełnym ekranie' : 'Open Fullscreen'}</span>
            <ExternalLink className="w-3 h-3 opacity-80" />
          </a>
        </div>
      </div>

      {/* Notification banner if fullscreen was clicked without login */}
      {fullscreenNotice && (
        <div className="p-3 bg-cyan-950/90 border border-cyan-400/50 rounded-xl text-cyan-200 text-xs flex items-center gap-2 animate-bounce">
          <Sparkles className="w-4 h-4 text-cyan-400 shrink-0" />
          <span>{fullscreenNotice}</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. VIEWPORT: PRESENTATION PICTURES (ON LOGIN) VS INTERACTIVE MAP (WHEN LOGGED IN) */}
      {/* ========================================================================= */}
      <div className="relative rounded-2xl overflow-hidden border-2 border-slate-700/80 bg-slate-950 shadow-2xl aspect-[16/10] sm:aspect-[16/9] min-h-[460px] max-h-[660px] w-full group">
        
        {/* Scenario A: User is logged in AND has access (Trial Active or Subscribed) */}
        {isLoggedIn && mapAccess.hasAccess ? (
          <>
            {/* Top trial status banner overlay */}
            <div className="absolute top-3 left-3 right-3 z-20 flex items-center justify-between pointer-events-none">
              <div className="bg-slate-900/95 backdrop-blur-md border border-emerald-500/60 text-white px-3.5 py-1.5 rounded-xl text-xs font-mono flex items-center gap-2 shadow-xl">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping"></span>
                <span className="font-bold text-emerald-300">
                  {mapAccess.isSubscribed
                    ? '🛡️ ПІДПИСКА СИТУАЦІЙНОГО ЦЕНТРУ АКТИВНА 24/7'
                    : `🟢 1 ДЕНЬ БЕЗКОШТОВНОГО ДОСТУПУ АКТИВНИЙ • Залишилось: ${mapAccess.trialHoursRemaining} год.`}
                </span>
              </div>
              <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700/80 text-cyan-300 px-3 py-1.5 rounded-lg text-xs font-mono hidden md:flex items-center gap-2 shadow-lg">
                <span>{activeMapTab === 'situational' ? '📡 Live FIRMS + РЕБ GPSJam' : '🛰️ Sentinel-2 L2A Multispectral'}</span>
              </div>
            </div>

            {/* Live interactive iframe */}
            <iframe
              src={(activeMapTab === 'situational' ? '/situational-map.html' : '/nis-eco-map.html') + '?pwd=bass123123'}
              title="Situational Map"
              className="w-full h-full border-0"
              loading="lazy"
            />

            {/* Bottom action strip */}
            <div className="absolute bottom-3 left-3 right-3 z-10 pointer-events-auto flex flex-wrap items-center justify-between gap-2 p-2.5 bg-slate-900/90 backdrop-blur-md rounded-xl border border-slate-700/80">
              <div className="text-left text-xs text-slate-300 font-medium">
                <span className="text-emerald-400 font-bold">✓ Інтерактивний режим онлайн:</span> {isUk ? 'переміщуйтесь по карті, змінюйте масштаб або натискайте на спалахи для точних координат' : 'pan, zoom, and inspect live thermal/EW radar points directly'}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOrderPlan('nis-eco-situational-analyst')}
                  className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>{isUk ? 'Сформувати Радар-Звіт Word' : 'Generate Radar Report'}</span>
                </button>
              </div>
            </div>
          </>
        ) : isLoggedIn && !mapAccess.hasAccess ? (
          /* Scenario B: User logged in, but 1-day free trial has expired and no paid subscription */
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Background presentation picture blurred */}
            <img 
              src={activeMapTab === 'situational' ? '/img/situational-map-preview.png' : '/img/nis-eco-map-preview.png'} 
              alt="Preview Map"
              className="w-full h-full object-cover object-center filter blur-sm brightness-40"
            />
            <div className="absolute inset-0 bg-slate-950/70" />

            <div className="relative z-10 max-w-lg mx-auto p-6 bg-slate-900/95 border-2 border-amber-500/60 rounded-3xl text-center space-y-4 shadow-2xl backdrop-blur-md">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/40">
                <Clock className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-black text-white">
                  {isUk ? '⏱️ Термін 1-денного безкоштовного доступу вичерпано' : '⏱️ 1-Day Free Trial Has Expired'}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isUk ? 'Ваш 24-годинний пробний доступ до інтерактивної карти завершився. Оберіть разовий радар-звіт або підключіть щомісячний моніторинг для продовження роботи.' : 'Your 24-hour test period has ended. Choose a plan to unlock 24/7 telemetry.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <button
                  onClick={() => handleOrderPlan('nis-eco-situational-analyst')}
                  className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-extrabold text-xs rounded-xl shadow cursor-pointer"
                >
                  <span>{isUk ? '⚡ Разовий Звіт (190 грн)' : 'Single Report (190 UAH)'}</span>
                </button>
                <button
                  onClick={() => onOpenPricing && onOpenPricing()}
                  className="w-full sm:w-auto px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-cyan-500/40 font-bold text-xs rounded-xl shadow cursor-pointer"
                >
                  <span>{isUk ? '🏆 Тарифи Ситуаційного Центру' : 'View Plans'}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Scenario C: On Login Page (NOT LOGGED IN) -> PRESENTATION PICTURES WITH HIGH-TECH PREVIEW MOCKUP */
          <div className="relative w-full h-full flex flex-col justify-between overflow-hidden">
            
            {/* Presentation Image Layer */}
            <img 
              src={activeMapTab === 'situational' ? '/img/situational-map-preview.png' : '/img/nis-eco-map-preview.png'} 
              alt={activeMapTab === 'situational' ? 'Оперативна ситуаційна карта загроз ТВД' : 'Супутниковий моніторинг НІС-ЕКО'}
              className="absolute inset-0 w-full h-full object-cover object-center filter brightness-95 contrast-105 transition-transform duration-700 group-hover:scale-105"
            />

            {/* Tactical Grid and Vignette Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/60 pointer-events-none" />

            {/* Top Bar on Presentation Picture */}
            <div className="relative z-10 p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="bg-slate-900/90 backdrop-blur-md border border-cyan-500/60 text-white px-3 py-1.5 rounded-lg text-xs font-mono flex items-center gap-2 shadow-lg">
                  <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse"></span>
                  <span className="font-bold text-cyan-300">
                    {isUk ? '📸 ПРЕЗЕНТАЦІЙНИЙ ОГЛЯД КАРТИ' : '📸 PRESENTATION PREVIEW MOCKUP'}
                  </span>
                </div>
                <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-md bg-slate-900/80 border border-slate-700 text-slate-300 text-[10px] font-mono">
                  {activeMapTab === 'situational' ? 'NASA FIRMS 24H · РЕБ GPSJam' : 'Sentinel-2 NDVI · NDWI'}
                </span>
              </div>

              <div className="bg-amber-950/90 border border-amber-500/60 text-amber-300 px-3 py-1 rounded-lg text-[11px] font-mono font-bold flex items-center gap-1.5 shadow-md">
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>{isUk ? 'Живий інтерактив: після входу' : 'Live interactive: upon login'}</span>
              </div>
            </div>

            {/* Center Floating Glassmorphism Access Card */}
            <div className="relative z-10 max-w-xl mx-auto p-5 sm:p-6 bg-slate-900/90 border border-cyan-500/50 rounded-2xl shadow-2xl backdrop-blur-xl text-center space-y-4 my-auto">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-600/30 to-blue-600/30 text-cyan-400 border border-cyan-400/50 shadow-inner">
                <Lock className="w-6 h-6 animate-pulse text-cyan-300" />
              </div>

              <div className="space-y-1.5">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-950/90 border border-emerald-400/50 text-emerald-300 text-[11px] font-mono font-bold">
                  <span>🎁</span>
                  <span>{isUk ? '1 ДЕНЬ 100% БЕЗКОШТОВНОГО ДОСТУПУ ДЛЯ ВСІХ' : '1-DAY 100% FREE TRIAL FOR ALL USERS'}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                  {isUk ? 'Повний інтерактивний доступ до карт активується після входу' : 'Full Interactive Map Access Activates After Login'}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed max-w-md mx-auto">
                  {isUk 
                    ? 'Увійдіть без пароля (в 1 клік), щоб отримати 24 години повного безкоштовного інтерактиву: масштабування пожеж NASA FIRMS, радіуси РЕБ та супутникові спектри NDVI «НІС-ЕКО».'
                    : 'Log in with zero friction (1-click) to activate 24 hours of unrestricted live interactive satellite tracking.'}
                </p>
              </div>

              {/* Feature check list */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-200 text-left pt-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span>{isUk ? 'Масштабування та точні координати' : 'Zoom & exact GPS coordinates'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span>{isUk ? 'Теплові вогнища NASA FIRMS 24/72h' : 'NASA FIRMS 24/72h live thermal'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span>{isUk ? 'Зони глушіння навігації РЕБ GPSJam' : 'GPSJam electronic warfare'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span>{isUk ? 'Спектральні індекси Sentinel-2 NDVI' : 'Sentinel-2 multispectral NDVI'}</span>
                </div>
              </div>

              {/* CTA Button to Login */}
              <div className="pt-2">
                <button
                  onClick={handleGoToLogin}
                  className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 hover:from-cyan-400 hover:to-blue-500 text-white font-black text-xs sm:text-sm rounded-xl transition-all shadow-xl shadow-cyan-900/60 flex items-center justify-center gap-2 group cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                  <span>{isUk ? '🚀 Увійти та активувати 1 день безкоштовно' : '🚀 Log in & Activate 1 Free Day'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Bottom preview note strip */}
            <div className="relative z-10 p-2.5 sm:p-3 bg-slate-900/90 backdrop-blur-md border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400 font-mono">
              <div>
                <span className="text-cyan-400 font-bold">ℹ️ Презентаційний режим:</span> {isUk ? 'натисніть «Увійти», щоб перейти до інтерактивного режиму' : 'click "Log in" to interact with live maps'}
              </div>
              <div className="text-emerald-400 font-bold">
                🎁 Без банківської карти • Старт за 15 сек
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 2. ОФІЦІЙНА ЗАФІКСОВАНА ТАРИФНА СІТКА СИТУАЦІЙНОГО ЦЕНТРУ */}
      {/* ========================================================================= */}
      <div className="pt-4 space-y-6 relative z-10 text-left">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-cyan-400 font-mono text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span>{isUk ? 'ОФІЦІЙНІ ЗАФІКСОВАНІ ТАРИФИ СИТУАЦІЙНОГО ЦЕНТРУ' : isPl ? 'OFICJALNE STAŁE TARYFY CENTRUM SYTUACYJNEGO' : 'OFFICIAL FIXED SITUATIONAL CENTER PRICING'}</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {isUk ? 'Оберіть формат оперативного контролю вашого бізнесу' : isPl ? 'Wybierz format monitoringu operacyjnego' : 'Choose Your Operational Monitoring Plan'}
          </h3>
          <p className="text-xs sm:text-sm text-slate-400">
            {isUk ? '1-й день після входу — 100% БЕЗКОШТОВНО. Далі — гарантована фіксація цін: онлайн-оплата карткою, миттєва активація доступу та вивантаження звітів за ДСТУ 4163:2020.' : 
             '1st day after login is 100% FREE. Guaranteed price lock, card checkout, instant access, and DSTU 4163:2020 certified reporting.'}
          </p>
        </div>

        {/* 4 Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          
          {/* Card 1: Single Check */}
          <div className="bg-slate-800/80 border border-slate-700/80 hover:border-cyan-500/50 rounded-2xl p-5 flex flex-col justify-between transition-all hover:shadow-xl hover:-translate-y-1 group">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-1 rounded-md bg-slate-700/60 text-slate-300 text-xs font-mono font-semibold">
                  {isUk ? 'МІКРО / ТЕСТ' : 'SINGLE CHECK'}
                </span>
                <span className="text-xl">⚡</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white group-hover:text-cyan-400 transition-colors">
                  {isUk ? 'Разовий Радар-Звіт' : isPl ? 'Jednorazowy Raport' : 'Single Radar Check'}
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  {isUk ? 'Для експрес-перевірки конкретної точки або поля до 50 км.' : 'Quick audit of a single field or coordinate radius.'}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-700/60">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-white">190</span>
                  <span className="text-sm font-bold text-cyan-400">грн</span>
                  <span className="text-xs text-slate-400 ml-1">/ разово</span>
                </div>
              </div>
              <ul className="space-y-2.5 text-xs text-slate-300 pt-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{isUk ? '1 повний радар-звіт по координатах' : '1 complete coordinate radar report'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Термальні пожежі NASA FIRMS за 72 год' : 'NASA FIRMS 72h thermal fire tracking'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Оцінка рівня глушіння РЕБ GPSJam' : 'GPSJam EW jamming level assessment'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Знімок Sentinel-2 та експорт Word/PDF' : 'Sentinel-2 imagery & Word/PDF export'}</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => handleOrderPlan('nis-eco-situational-analyst')}
              className="mt-6 w-full py-2.5 px-4 bg-slate-700 hover:bg-cyan-600 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 shadow cursor-pointer"
            >
              <span>{isUk ? 'Замовити Звіт (190 грн)' : 'Order Report (190 UAH)'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Card 2: PRO Monthly (BESTSELLER) */}
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-cyan-500 rounded-2xl p-5 flex flex-col justify-between transition-all hover:shadow-2xl hover:shadow-cyan-900/30 hover:-translate-y-1 relative group">
            {/* Bestseller Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black text-[10px] tracking-wider uppercase px-3 py-1 rounded-full shadow-md">
              {isUk ? '🔥 НАЙПОПУЛЯРНІШИЙ B2B' : '🔥 BESTSELLER B2B'}
            </div>

            <div className="space-y-4 pt-1">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-1 rounded-md bg-cyan-950 text-cyan-400 text-xs font-mono font-semibold border border-cyan-500/30">
                  {isUk ? 'СТАНДАРТ ДЛЯ БІЗНЕСУ' : 'BUSINESS STANDARD'}
                </span>
                <span className="text-xl">🏆</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white group-hover:text-cyan-400 transition-colors">
                  {isUk ? 'Оперативний Моніторинг PRO' : isPl ? 'Monitoring Operacyjny PRO' : 'Operational Monitoring PRO'}
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  {isUk ? 'Для агрофірм, елеваторів, перевізників та промислових складів.' : 'For agrobusiness, logistics, warehouses & fleets.'}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-700/60">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-cyan-400">1 950</span>
                  <span className="text-sm font-bold text-white">грн</span>
                  <span className="text-xs text-slate-400 ml-1">/ місяць</span>
                </div>
              </div>
              <ul className="space-y-2.5 text-xs text-slate-200 pt-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span className="font-semibold">{isUk ? 'Онлайн-доступ до карти 24/7' : '24/7 online interactive map access'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Моніторинг до 15 закріплених об\'єктів' : 'Monitor up to 15 assigned assets/fields'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Сповіщення в Telegram про пожежі/РЕБ' : 'Telegram alerts on fires or EW spikes'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'До 60 детальних звітів Word на місяць' : 'Up to 60 detailed Word reports monthly'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Експорт векторних шарів GeoJSON / KML' : 'GeoJSON / KML vector layer exports'}</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => handleOrderPlan('nis-eco-situational-analyst')}
              className="mt-6 w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/50 cursor-pointer"
            >
              <span>{isUk ? 'Підключити PRO (1 950 грн/міс)' : 'Activate PRO (1,950 UAH/mo)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Card 3: Corporate Enterprise */}
          <div className="bg-slate-800/80 border border-slate-700/80 hover:border-purple-500/50 rounded-2xl p-5 flex flex-col justify-between transition-all hover:shadow-xl hover:-translate-y-1 group">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-1 rounded-md bg-purple-950/80 text-purple-300 text-xs font-mono font-semibold border border-purple-500/30">
                  {isUk ? 'КОРПОРАТИВНИЙ' : 'ENTERPRISE'}
                </span>
                <span className="text-xl">🛡️</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors">
                  {isUk ? 'Корпоративний Ситуаційний Центр' : isPl ? 'Centrum Korporacyjne' : 'Enterprise Situational Suite'}
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  {isUk ? 'Для холдингів, заводів, енергетики та служб безпеки.' : 'For enterprise holdings, factories, energy & security teams.'}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-700/60">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-purple-400">8 900</span>
                  <span className="text-sm font-bold text-white">грн</span>
                  <span className="text-xs text-slate-400 ml-1">/ місяць</span>
                </div>
              </div>
              <ul className="space-y-2.5 text-xs text-slate-300 pt-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Необмежена кількість об\'єктів моніторингу' : 'Unlimited asset & logistics monitoring'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'До 5 робочих місць аналітиків безпеки' : 'Up to 5 security analyst seats'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Радарні SAR-знімки деформацій рельєфу' : 'Radar SAR interferometry & ground deformation'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Прямий API-ендпоінт для 1C / ERP / BAS' : 'Direct API integration endpoint for 1C/ERP'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Пріоритетний інженерний супровід 24/7' : '24/7 dedicated engineering support'}</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => handleOrderPlan('nis-eco-pro-orchestrator')}
              className="mt-6 w-full py-2.5 px-4 bg-purple-900/60 hover:bg-purple-600 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 border border-purple-500/40 shadow cursor-pointer"
            >
              <span>{isUk ? 'Підключити Enterprise (8 900 грн/міс)' : 'Get Enterprise (8,900 UAH/mo)'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Card 4: War Damage Audit */}
          <div className="bg-slate-800/80 border border-slate-700/80 hover:border-amber-500/50 rounded-2xl p-5 flex flex-col justify-between transition-all hover:shadow-xl hover:-translate-y-1 group">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-1 rounded-md bg-amber-950/80 text-amber-300 text-xs font-mono font-semibold border border-amber-500/30">
                  {isUk ? 'СПЕЦ-ПОСЛУГА' : 'EXPERT AUDIT'}
                </span>
                <span className="text-xl">⚖️</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">
                  {isUk ? 'Акт Оцінки Воєнних Збитків' : isPl ? 'Akt Oceny Szkód Wojennych' : 'War Damage Audit Act'}
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  {isUk ? 'Юридично завірений акт для судів, компенсацій та страхових.' : 'Legally certified act for compensation claims & court.'}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-700/60">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-amber-400">4 500</span>
                  <span className="text-sm font-bold text-white">грн</span>
                  <span className="text-xs text-slate-400 ml-1">/ за об\'єкт</span>
                </div>
              </div>
              <ul className="space-y-2.5 text-xs text-slate-300 pt-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Супутниковий аудит ДО та ПІСЛЯ події' : 'Pre/Post satellite comparison package'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Спектральний аналіз NBR/NDVI збитків' : 'Spectral NBR/NDVI damage quantification'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Термальний доказовий слід NASA FIRMS' : 'NASA FIRMS forensic thermal strike proof'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{isUk ? '100% за стандартом ДСТУ 4163:2020' : '100% DSTU 4163:2020 & court compliant'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{isUk ? 'Готовий для подання на держкомпенсацію' : 'Certified for state & international claims'}</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => handleOrderPlan('nis-eco-pro-orchestrator')}
              className="mt-6 w-full py-2.5 px-4 bg-amber-900/60 hover:bg-amber-600 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 border border-amber-500/40 shadow cursor-pointer"
            >
              <span>{isUk ? 'Замовити Акт Збитків (4 500 грн)' : 'Order Damage Audit (4,500 UAH)'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SituationalCenterShowcase;
